import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const Services = () => {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");

  const servicesData = {
    carpentry: {
      title: "Carpentry & Joinery",
      description:
        "Custom cabinetry, furniture, and wood installations for homes and businesses.",
      images: [
        "/img/land.webp",
        "/img/bed3.webp",
        "/img/ward.jpg",
        "/img/sofacum.webp",
      ],
      features: [
        "Custom cabinetry & wardrobes",
        "Kitchen and office fittings",
        "Doors, frames & wooden installations",
        "High-quality finishing and materials",
      ],
      icon: "hammer",
    },

    transport: {
      title: "TRANSPORT & LOGISTICS",
      description:
        "Reliable transport solutions for individuals and businesses with professional drivers and dependable scheduling.",
      images: ["/img/transport1.avif"],
      features: [
        "Local and long-distance transport",
        "Professional drivers",
        "Well-maintained vehicles",
        "Flexible scheduling",
      ],
      icon: "truck",
    },

    airport: {
      title: "AIRPORT TRANSFERS & SHUTTLES",
      description:
        "Comfortable and reliable airport pickup and drop-off services for individuals and groups.",
      images: ["/img/shuttle1.jpg", "/img/airport1.jpg"],
      features: [
        "Airport pickup & drop-off",
        "Private or group transfers",
        "On-time reliable service",
        "Professional drivers",
      ],
      icon: "plane",
    },

    rentals: {
      title: "VEHICLE RENTALS",
      description:
        "Affordable vehicle rental services for short-term or long-term travel needs.",
      images: ["/img/rental.jpg"],
      features: [
        "Short-term vehicle rentals",
        "Long-term rental options",
        "Well-maintained vehicles",
        "Flexible rental agreements",
      ],
      icon: "car",
    },
  };

  const categories = Object.keys(servicesData).map((key) => ({
    id: key,
    ...servicesData[key],
  }));

  const [activeCategory, setActiveCategory] = useState(tab || "carpentry");

  useEffect(() => {
    if (tab && servicesData[tab]) {
      setActiveCategory(tab);
    }
  }, [tab]);

  const activeService = servicesData[activeCategory];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-primary py-20">
        <div className="absolute inset-0 bg-black/20"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-semibold mb-6">
              OUR SERVICES
            </span>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Professional{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                Solutions
              </span>
            </h1>

            <p className="text-xl text-gray-200 mb-8">
              Carpentry, transport logistics, airport transfers and vehicle
              rental services delivered with reliability and precision.
            </p>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="sticky top-20 z-40 bg-white/95 backdrop-blur-md shadow-lg py-4">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto pb-2 space-x-2 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex-shrink-0 px-6 py-3 rounded-xl transition-all duration-300 flex items-center gap-3 whitespace-nowrap ${
                  activeCategory === category.id
                    ? "bg-gradient-to-r from-primary to-accent text-white shadow-lg scale-105"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <i className={`fas fa-${category.icon}`}></i>
                <span className="font-semibold">{category.title}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Title + Description */}
          <div className="px-8 pt-10 pb-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {activeService.title}
            </h2>

            <p className="text-gray-600 text-lg max-w-3xl">
              {activeService.description}
            </p>
          </div>

          {/* Image Gallery */}
          <div className="grid md:grid-cols-2 gap-4 px-8 pb-10">
            {activeService.images.map((img, index) => (
              <div
                key={index}
                className="relative overflow-hidden rounded-2xl group"
              >
                <img
                  src={img}
                  alt={activeService.title}
                  className="w-full h-72 object-cover group-hover:scale-110 transition duration-700"
                />

                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition"></div>
              </div>
            ))}
          </div>

          {/* Features */}
          <div className="px-8 pb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              What We Offer
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              {activeService.features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-gray-50 p-5 rounded-xl"
                >
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <i className="fas fa-check text-primary"></i>
                  </div>

                  <span className="text-gray-700 font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-primary/5 to-accent/5 p-10 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Need This Service?
            </h3>

            <a
              href="http://wa.me/264852000821"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-gray-900 font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 inline-flex items-center justify-center gap-3 border border-gray-200"
            >
              <i className="fab fa-whatsapp text-green-500"></i>
              <span>Chat on WhatsApp</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
